<template>
  <div>
    <b-card>
      <b-row class="mt-1">
        <b-col xl="3" lg="3" md="4" sm="12" cols="12">
          <b-button @click="openAddModal" block variant="primary">
            <feather-icon icon="PlusIcon" class="mr-50" />
            <span class="align-middle">Add Half Day Policy</span>
          </b-button>
        </b-col>
        <b-col xl="9" lg="9" md="8" sm="12" cols="12">
          <b-row>
            <b-col md="4">
              <v-select
                v-model="filters.departmentID"
                :options="departmentOptions"
                :clearable="true"
                label="name"
                :reduce="(val) => val.id"
                placeholder="Select department"
                @input="onFilterChange"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <!-- Data Grid -->
      <b-table
        class="mt-1"
        :items="policies"
        :fields="fields"
        :busy="dataLoading"
        responsive
        hover
        show-empty
        empty-text="No half day policies found"
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner variant="primary" />
          </div>
        </template>
        <template #cell(actions)="{ item }">
          <div class="">
            <b-button
              variant="primary"
              v-b-tooltip.hover.left
              title="Edit"
              class="btn-icon mr-1"
              @click="editPolicy(item)"
            >
              <feather-icon icon="EditIcon" />
            </b-button>

            <b-button
              variant="outline-danger"
              v-b-tooltip.hover.right
              title="Delete"
              class="btn-icon"
              @click="deletePolicy(item)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>
        </template>
        <template #cell(sno)="{ index }">
          {{ (currentPage - 1) * perPage + index + 1 }}
        </template>

        <template #cell(startsAt)="{ value }">
          {{ formatTime(value) }}
        </template>

        <template #cell(endsAt)="{ value }">
          {{ formatTime(value) }}
        </template>
      </b-table>

      <!-- Pagination -->
      <b-col cols="12" class="d-flex align-items-center justify-content-center">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="perPage"
          first-number
          last-number
          class="mb-0 mt-1 mt-sm-0"
          prev-class="prev-item"
          next-class="next-item"
          @change="loadPolicies"
        >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="18" />
          </template>
        </b-pagination>
      </b-col>
    </b-card>

    <!-- Add/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="isEdit ? 'Edit Half Day Policy' : 'Add Half Day Policy'"
      @hidden="resetForm"
      centered
      no-close-on-backdrop
    >
      <b-form @submit.prevent>
        <b-row>
          <b-col md="12">
            <b-form-group label="Department" label-for="department">
              <v-select
                v-model="form.departmentID"
                :options="isEdit ? departments : availableDepartments"
                :clearable="false"
                :disabled="isEdit"
                label="name"
                :reduce="(val) => val.id"
                placeholder="Select department"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group label="Half Day Start Time" label-for="startTime">
              <flat-pickr
                v-model="form.startTime"
                :key="`start-time-${form.id}`"
                class="form-control"
                :config="{
                  enableTime: true,
                  noCalendar: true,
                  altFormat: 'h:i K',
                  altInput: true,
                  dateFormat: 'Y-m-d\\TH:i:S',
                  defaultHour: 8,
                }"
                placeholder="Select start time"
                @input="onTimeInputChange"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Half Day End Time" label-for="endTime">
              <flat-pickr
                v-model="form.endTime"
                :key="`end-time-${form.id}`"
                class="form-control"
                :config="{
                  enableTime: true,
                  noCalendar: true,
                  altFormat: 'h:i K',
                  altInput: true,
                  dateFormat: 'Y-m-d\\TH:i:S',
                  defaultHour: 13,
                }"
                placeholder="Select end time"
                @input="onTimeInputChange"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showModal = false">
          Cancel
        </b-button>
        <b-button variant="primary" @click="savePolicy" :disabled="loading">
          <b-spinner v-if="loading" small />
          {{ isEdit ? "Update" : "Save" }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BPagination,
  BModal,
  BForm,
  BFormGroup,
  BSpinner,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { mapActions } from "vuex";

export default {
  name: "HalfDayPolicy",
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BPagination,
    BModal,
    BForm,
    BFormGroup,
    BSpinner,
    vSelect,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      policies: [],
      allPolicies: [], // Store all policies for available departments calculation
      departments: [],
      departmentOptions: [],
      searchQuery: "",
      showModal: false,
      isEdit: false,
      loading: false,
      dataLoading: false,
      currentPage: 1,
      perPage: 30,
      totalCount: 0,
      fields: [
        { key: "sno", label: "S.No" },
        { key: "departmentName", label: "Department" },
        { key: "startsAt", label: "Start Time" },
        { key: "endsAt", label: "End Time" },
        { key: "actions", label: "Actions" },
      ],
      form: {
        id: 0,
        departmentID: null,
        startTime: "",
        endTime: "",
        campusID: this.$store.state.userData.cId,
      },
      filters: {
        departmentID: null,
      },
    };
  },
  created() {
    this.loadAllPolicies(); // Load all policies first
    this.loadPolicies();
    this.loadDepartments();
  },
  computed: {
    availableDepartments() {
      // Get department IDs that already have policies from ALL policies (not filtered)
      const usedDepartmentIds = this.allPolicies.map(
        (policy) => policy.departmentID
      );

      // Filter out departments that already have policies
      return this.departments.filter(
        (dept) => !usedDepartmentIds.includes(dept.id)
      );
    },
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    async loadAllPolicies() {
      // Load all policies without any filter for available departments calculation
      const obj = {
        url:
          this.$store.state.domain +
          "HalfDayPolicy/GetByPagination?db=" +
          this.$store.state.userData.db +
          "&campusID=" +
          this.$store.state.userData.cId +
          "&page=1" +
          "&pageSize=1000" + // Get all policies
          "&departmentID=0", // No department filter
        token: this.$store.state.userData.token,
        dashboard: true,
      };
      let res = await this.get(obj);
      if (res && Array.isArray(res.data)) {
        this.allPolicies = res.data;
      } else {
        this.allPolicies = [];
      }
    },

    async loadDepartments() {
      const obj = {
        url:
          this.$store.state.domain +
          "StaffDepartments?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.departments = await this.get(obj);
      // Add "All Departments" option for filter
      this.departmentOptions = [
        { id: 0, name: "All Departments" },
        ...this.departments,
      ];
    },

    async loadPolicies() {
      this.dataLoading = true;
      const obj = {
        url:
          this.$store.state.domain +
          "HalfDayPolicy/GetByPagination?db=" +
          this.$store.state.userData.db +
          "&campusID=" +
          this.$store.state.userData.cId +
          "&page=" +
          this.currentPage +
          "&pageSize=" +
          this.perPage +
          "&departmentID=" +
          (this.filters.departmentID || 0),
        token: this.$store.state.userData.token,
        dashboard: true,
      };
      let res = await this.get(obj);
      if (res && Array.isArray(res.data)) {
        this.policies = res.data;
        this.totalCount = res.total;
      } else {
        this.policies = [];
        this.totalCount = 0;
      }
      this.dataLoading = false;
    },

    onFilterChange() {
      this.currentPage = 1; // Reset to first page when filters change
      this.loadPolicies();
    },

    openAddModal() {
      this.isEdit = false;
      this.form.departmentID = null; // Reset department selection
      this.showModal = true;
    },

    editPolicy(item) {
      this.isEdit = true;
      this.form = {
        ...item,
        startTime: this.buildTimeISOForFlatpickr(item.startsAt),
        endTime: this.buildTimeISOForFlatpickr(item.endsAt),
      };
      this.showModal = true;
    },

    async deletePolicy(item) {
      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        const obj = {
          url:
            this.$store.state.domain +
            "HalfDayPolicy/" +
            item.id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Half day policy deleted successfully.",
          context: this,
          token: this.$store.state.userData.token,
        };
        const success = await this.delete(obj);
        if (success) {
          this.loadAllPolicies(); // Update all policies
          this.loadPolicies();
        }
      }
    },

    async savePolicy() {
      if (
        !this.form.departmentID ||
        !this.form.startTime ||
        !this.form.endTime
      ) {
        this.$bvToast.toast("Please fill all required fields!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.loading = true;

      // Format time values to HH:MM:SS format only
      const formatTimeOnly = (timeString) => {
        if (!timeString) return "00:00:00";

        // Remove any extra spaces
        timeString = timeString.trim();

        // If it's in ISO format (YYYY-MM-DDTHH:MM:SS), extract time part
        if (timeString.includes("T")) {
          const timePart = timeString.split("T")[1];
          return timePart || "00:00:00";
        }

        // If already in HH:MM:SS format, return as is
        if (timeString.match(/^\d{1,2}:\d{2}:\d{2}$/)) {
          return timeString;
        }

        // If in HH:MM format, add seconds
        if (timeString.match(/^\d{1,2}:\d{2}$/)) {
          return timeString + ":00";
        }

        // Default fallback
        return "00:00:00";
      };

      // Prepare the request body with time only
      const requestBody = {
        id: this.form.id,
        departmentID: this.form.departmentID,
        startsAt: formatTimeOnly(this.form.startTime),
        endsAt: formatTimeOnly(this.form.endTime),
        campusID: this.form.campusID,
      };

      const obj = {
        url:
          this.$store.state.domain +
          "HalfDayPolicy/Save?db=" +
          this.$store.state.userData.db +
          "&campusID=" +
          this.form.campusID +
          "&userID=" +
          this.$store.state.userData.userID,
        body: requestBody,
        message:
          "Half day policy " +
          (this.isEdit ? "updated" : "created") +
          " successfully.",
        context: this,
        token: this.$store.state.userData.token,
      };

      const success = await this.post(obj);

      if (success) {
        this.showModal = false;
        this.loadAllPolicies(); // Update all policies
        this.loadPolicies();
      }
      this.loading = false;
    },

    resetForm() {
      this.form = {
        id: 0,
        departmentID: null,
        startTime: "",
        endTime: "",
        campusID: this.$store.state.userData.cId,
      };
    },

    buildTimeISOForFlatpickr(timeString) {
      // Expects timeString like "08:00:00" and returns "YYYY-MM-DDTHH:mm:ss"
      try {
        if (!timeString) return "";
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        // Ensure we have HH:mm:ss
        const parts = timeString.split(":");
        const hh = String(parseInt(parts[0] || "0", 10)).padStart(2, "0");
        const mi = String(parseInt(parts[1] || "0", 10)).padStart(2, "0");
        const ss = String(
          parseInt((parts[2] || "0").replace(/\D/g, ""), 10)
        ).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;
      } catch (e) {
        return "";
      }
    },

    onTimeInputChange() {
      // When user manually changes time inputs, we can add any additional logic here
      // For now, this just ensures the form is reactive to changes
      console.log(
        "Time input changed:",
        this.form.startTime,
        this.form.endTime
      );
    },

    formatTime(timeString) {
      if (!timeString) return "";

      try {
        // Extract time parts from various formats
        let hours, minutes;

        if (timeString.includes(":")) {
          const parts = timeString.split(":");
          hours = parseInt(parts[0], 10);
          minutes = parts[1] || "00";
        } else {
          return timeString; // Return as is if no colon found
        }

        // Convert to 12-hour format with AM/PM
        const period = hours >= 12 ? "PM" : "AM";
        const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

        return `${displayHours}:${minutes} ${period}`;
      } catch (e) {
        return timeString; // Return original if conversion fails
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";
</style>
